import React from "react";

import {
  faBracketsCurly,
  faUpRightFromSquare,
} from "@fortawesome/pro-regular-svg-icons";
import { useFormContext } from "react-hook-form";

import { AgentNodeForm, SchemaError } from "src/agentNode/types";
import { FieldErrors } from "src/api/types";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { EmptyState } from "src/design-system/EmptyState";
import {
  SCHEMA_MAPPING_ARROW_WIDTH,
  SchemaProperty,
} from "src/parentFlowNodes/SchemaProperty";
import { buildFieldNameToIdMap } from "src/parentFlowNodes/utils";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToAgentVersionPage } from "src/router/urls";
import { SchemaUI } from "src/schema/schemaMappingUtils";

export type AgentInputMappingsProps = {
  inputSchema: SchemaUI | undefined;
  agentName: string;
  agentId: string;
  versionId: string;
  immutable: boolean;
  isLoading: boolean;
  inputMappingErrors: Record<string, SchemaError>;
  runFieldErrors?: FieldErrors;
};

export const AgentInputMappings: React.FC<AgentInputMappingsProps> = ({
  inputSchema,
  agentName,
  agentId,
  versionId,
  immutable,
  isLoading,
  inputMappingErrors,
  runFieldErrors,
}) => {
  if (isLoading) {
    return <InputLoadingSkeleton />;
  }

  return (
    <AgentDataMap
      agentId={agentId}
      agentName={agentName}
      fieldMappingErrors={inputMappingErrors}
      readonly={immutable}
      runFieldErrors={runFieldErrors}
      schema={inputSchema}
      title="Inputs"
      versionId={versionId}
    />
  );
};

type AgentDataMapProps = {
  title: string;
  schema: SchemaUI | undefined;
  agentName: string;
  agentId: string;
  versionId: string;
  readonly: boolean;
  fieldMappingErrors: Record<string, SchemaError>;
  runFieldErrors?: FieldErrors;
};

const AgentDataMap: React.FC<AgentDataMapProps> = ({
  title,
  schema,
  agentName,
  agentId,
  versionId,
  readonly,
  fieldMappingErrors,
  runFieldErrors,
}) => {
  const { watch } = useFormContext<AgentNodeForm>();
  const inputMappings = watch("input_mappings") ?? {};
  const fieldNameToId = buildFieldNameToIdMap(inputMappings);

  const hasSchema = schema && schema.properties.length > 0;

  if (!hasSchema) {
    return (
      <div className="flex flex-col gap-y-2">
        <span className="font-inter-semibold-13px text-gray-800">{title}</span>
        <MissingAgentSchema
          agentId={agentId}
          agentName={agentName}
          versionId={versionId}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-2">
      <span className="font-inter-semibold-13px text-gray-800">{title}</span>
      <Card variant="default">
        <Card.Content>
          <div className="flex flex-col gap-y-2">
            <div className="mb-1 flex w-full gap-x-3">
              <p className="w-1/2 font-inter-normal-12px text-gray-500">
                Current Decision Flow
              </p>
              <div className={SCHEMA_MAPPING_ARROW_WIDTH} />
              <p className="w-1/2 font-inter-normal-12px text-gray-500">
                {agentName}
              </p>
            </div>
            {schema.properties.map((field) => {
              const fieldId = fieldNameToId[field.fieldName];
              const compilationError = runFieldErrors?.[fieldId];

              return (
                <SchemaProperty
                  key={field.fieldName}
                  field={field}
                  fieldError={Boolean(fieldMappingErrors[field.fieldName])}
                  highlighted={false}
                  isLoopNode={false}
                  isOutput={false}
                  lazy={false}
                  readonly={readonly}
                  runFieldError={compilationError}
                  schemaTabUrl=""
                  shouldDisplayError={false}
                />
              );
            })}
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

const InputLoadingSkeleton: React.FC = () => (
  <div className="flex flex-col gap-y-2">
    <SkeletonPlaceholder height="h-4" width="w-16" />
    <Card>
      <Card.Content>
        <div className="flex flex-col gap-y-2">
          <SkeletonPlaceholder height="h-8" />
          <SkeletonPlaceholder height="h-8" />
        </div>
      </Card.Content>
    </Card>
  </div>
);

type MissingAgentSchemaProps = {
  agentName: string;
  agentId: string;
  versionId: string;
};

const MissingAgentSchema: React.FC<MissingAgentSchemaProps> = ({
  agentName,
  agentId,
  versionId,
}) => {
  const { orgId, workspace } = useAuthoringContext();

  const url = `${getBaseUrl()}${getUrlToAgentVersionPage(
    orgId,
    workspace.id,
    agentId,
    versionId,
  )}`;

  return (
    <EmptyState
      action={
        <a
          data-loc="missing-agent-schema-action"
          href={url}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Button iconLeft={faUpRightFromSquare} variant="secondary">
            Define Schema
          </Button>
        </a>
      }
      dataLoc="missing-agent-schema"
      description={
        <>
          Define an input schema for{" "}
          <span className="font-inter-medium-12px">{agentName}</span> to
          complete mapping.
        </>
      }
      headline="Missing Schema"
      icon={faBracketsCurly}
      size="compact"
    />
  );
};
